/**
 * hide-option-variant-filter.js
 *
 * [HIDE-OPTION CUSTOMIZATION]
 * Purpose: Intercepts the variant selection flow for products that use a reserved option
 * named "hide" to route variants between sales channels. On any online sales channel,
 * only variants where hide="Online" should be visible, priced, or purchasable.
 *
 * This file does NOT modify global.js, product-info.js, or product-form.js. Instead it
 * hooks into the existing PUB_SUB_EVENTS system already used by those files, ensuring
 * forward-compatibility with theme updates.
 *
 * POS SAFETY: This script only runs in the browser (online storefront). Shopify POS reads
 * variant data directly from the Shopify API and is completely unaffected by theme JS.
 *
 * How it works:
 * 1. On DOMContentLoaded, finds all <variant-selects> elements that have the
 *    data-online-variant-ids attribute (set by product-variant-picker.liquid only when
 *    the product has a "hide" option).
 * 2. Intercepts the 'change' event on those elements BEFORE the theme's own listener
 *    fires, using the capture phase.
 * 3. When a visible option (e.g., Size) changes, it finds the matching online-eligible
 *    variant (hide="Online" + matching other options) and re-maps the selection to that
 *    variant's option values, keeping the hidden "hide" input locked to "Online".
 * 4. Ensures the product-info.js fetch always resolves to an online-eligible variant by
 *    keeping the hidden "Online" radio checked at all times.
 */

(function () {
  'use strict';

  /**
   * [HIDE-OPTION CUSTOMIZATION]
   * Purpose: Parse the product's full variant data from the <script data-selected-variant>
   * tag injected by product-variant-picker.liquid. This gives us the complete variant
   * object graph (options array, availability, price, id) without an extra network request.
   *
   * @param {HTMLElement} variantSelects - The <variant-selects> custom element.
   * @returns {Object|null} The currently selected variant object, or null if not found.
   */
  function getSelectedVariantData(variantSelects) {
    const scriptTag = variantSelects.querySelector('script[data-selected-variant]');
    if (!scriptTag) return null;
    try {
      return JSON.parse(scriptTag.textContent);
    } catch (e) {
      console.warn('[hide-option-variant-filter] Could not parse selected variant JSON.', e);
      return null;
    }
  }

  /**
   * [HIDE-OPTION CUSTOMIZATION]
   * Purpose: Fetch the full product JSON from Shopify's product.js endpoint so we have
   * access to ALL variants (not just the currently selected one). We need this to find
   * the correct online-eligible variant when the customer changes a visible option.
   *
   * The result is cached on the variantSelects element to avoid redundant network requests
   * during a single page session.
   *
   * @param {HTMLElement} variantSelects - The <variant-selects> custom element.
   * @returns {Promise<Object>} Resolves to the full Shopify product JSON object.
   */
  async function fetchProductVariants(variantSelects) {
    if (variantSelects._hideOptionProductData) {
      return variantSelects._hideOptionProductData;
    }

    // [HIDE-OPTION CUSTOMIZATION]
    // Purpose: Derive the product URL from the existing data-product-url attributes on
    // option value inputs. This avoids hardcoding any URL and works for any product.
    const firstOptionInput = variantSelects.querySelector('[data-product-url]');
    if (!firstOptionInput) return null;

    const productUrl = firstOptionInput.dataset.productUrl;
    const baseUrl = productUrl.split('?')[0].replace(/\/$/, '');

    try {
      const response = await fetch(`${baseUrl}.js`);
      const data = await response.json();
      variantSelects._hideOptionProductData = data;
      return data;
    } catch (e) {
      console.warn('[hide-option-variant-filter] Could not fetch product JSON.', e);
      return null;
    }
  }

  /**
   * [HIDE-OPTION CUSTOMIZATION]
   * Purpose: Given the full product data and the currently selected values for all
   * VISIBLE options (i.e., all options except "hide"), find the variant that:
   *   a) Has hide="Online" (case-insensitive), AND
   *   b) Matches all the visible option values the customer has selected.
   *
   * This is the core matching function that enforces the channel routing rule.
   *
   * @param {Object} productData - Full Shopify product JSON (from /products/handle.js).
   * @param {number} hideOptionIndex - 0-based index of the "hide" option in product.options.
   * @param {Object} selectedVisibleOptions - Map of { optionIndex: selectedValue } for
   *   all non-hide options.
   * @returns {Object|null} The matching online-eligible variant, or null if none found.
   */
  function findOnlineVariant(productData, hideOptionIndex, selectedVisibleOptions) {
    return productData.variants.find((variant) => {
      // [HIDE-OPTION CUSTOMIZATION]
      // Purpose: Enforce the hide="Online" requirement. Variants with any other hide value
      // (e.g., "POS") must never be selected on the online storefront.
      const hideVal = (variant.options[hideOptionIndex] || '').toLowerCase();
      if (hideVal !== 'online') return false;

      // [HIDE-OPTION CUSTOMIZATION]
      // Purpose: Verify that all visible option selections match this variant. We iterate
      // over the selectedVisibleOptions map (keyed by 0-based option index) and confirm
      // each one matches the variant's corresponding option value.
      return Object.entries(selectedVisibleOptions).every(([optIdx, val]) => {
        return variant.options[parseInt(optIdx, 10)] === val;
      });
    }) || null;
  }

  /**
   * [HIDE-OPTION CUSTOMIZATION]
   * Purpose: Read the currently checked/selected values for all VISIBLE options from the
   * DOM. We skip the "hide" option (identified by hideOptionIndex) because it is always
   * locked to "Online" and should never be read from customer interaction.
   *
   * @param {HTMLElement} variantSelects - The <variant-selects> custom element.
   * @param {number} hideOptionIndex - 0-based index of the "hide" option.
   * @returns {Object} Map of { optionIndex: selectedValue } for all visible options.
   */
  function getSelectedVisibleOptions(variantSelects, hideOptionIndex) {
    const selected = {};

    // [HIDE-OPTION CUSTOMIZATION]
    // Purpose: Collect checked radio inputs (used for swatch and button picker types).
    variantSelects.querySelectorAll('fieldset:not([aria-hidden="true"]) input[type="radio"]:checked').forEach((input) => {
      // Option position is 1-based in the name attribute (e.g., "Size-1"), convert to 0-based index.
      const nameParts = input.name.split('-');
      const position = parseInt(nameParts[nameParts.length - 1], 10) - 1;
      if (position !== hideOptionIndex) {
        selected[position] = input.value;
      }
    });

    // [HIDE-OPTION CUSTOMIZATION]
    // Purpose: Collect selected dropdown values (used for dropdown and swatch_dropdown
    // picker types). Same position extraction logic as above.
    variantSelects.querySelectorAll('select.select__select').forEach((select) => {
      const nameParts = select.name.replace('options[', '').replace(']', '');
      // For dropdowns, derive position from the option index in product.options.
      // We use the select's id attribute: "Option-{sectionId}-{index0}"
      const idParts = select.id.split('-');
      const index0 = parseInt(idParts[idParts.length - 1], 10);
      if (index0 !== hideOptionIndex) {
        selected[index0] = select.value;
      }
    });

    return selected;
  }

  /**
   * [HIDE-OPTION CUSTOMIZATION]
   * Purpose: Ensure the hidden "hide" option radio input remains checked to "Online"
   * after any DOM manipulation or re-render. The product-info.js component may swap
   * option value HTML during variant changes; this function re-locks the hidden input
   * after each such update.
   *
   * @param {HTMLElement} variantSelects - The <variant-selects> custom element.
   */
  function lockHideOptionToOnline(variantSelects) {
    const hiddenFieldset = variantSelects.querySelector('fieldset[aria-hidden="true"]');
    if (!hiddenFieldset) return;

    hiddenFieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      // [HIDE-OPTION CUSTOMIZATION]
      // Purpose: Only the "Online" value should ever be checked. All other values in the
      // hidden fieldset are unchecked to prevent them from contributing to form submission
      // or variant resolution.
      input.checked = input.value.toLowerCase() === 'online';
    });
  }

  /**
   * [HIDE-OPTION CUSTOMIZATION]
   * Purpose: Main initialization function. Runs after the DOM is ready and sets up the
   * change event interceptor on each <variant-selects> element that has the
   * data-online-variant-ids attribute (i.e., products with a "hide" option).
   *
   * We use the capture phase (third argument: true) so our handler runs BEFORE the theme's
   * own 'change' listener in global.js (which uses bubble phase). This allows us to
   * correct the hidden "hide" input state before the theme reads selectedOptionValues.
   */
  function init() {
    document.querySelectorAll('variant-selects[data-online-variant-ids]').forEach((variantSelects) => {
      const hideOptionIndex = parseInt(variantSelects.dataset.hideOptionIndex, 10);

      // [HIDE-OPTION CUSTOMIZATION]
      // Purpose: Lock the hidden "hide" input to "Online" on initial page load, before
      // any customer interaction. This handles the case where the server-rendered HTML
      // might not have pre-checked the correct value (e.g., due to URL-based variant
      // selection resolving to a POS variant).
      lockHideOptionToOnline(variantSelects);

      // [HIDE-OPTION CUSTOMIZATION]
      // Purpose: Intercept change events in the capture phase so we can enforce the
      // hide="Online" lock before the theme's bubble-phase listener reads the form state.
      variantSelects.addEventListener('change', async (event) => {
        // [HIDE-OPTION CUSTOMIZATION]
        // Purpose: Ignore change events originating from the hidden "hide" fieldset itself
        // (e.g., if something programmatically triggers a change on it). We only need to
        // act on changes to visible options made by the customer.
        if (event.target.closest('fieldset[aria-hidden="true"]')) return;

        // [HIDE-OPTION CUSTOMIZATION]
        // Purpose: Re-lock the hidden "hide" input immediately after any visible option
        // changes. The theme's change handler will fire next (bubble phase) and read
        // selectedOptionValues — by this point, hide="Online" is guaranteed to be checked.
        lockHideOptionToOnline(variantSelects);

        // [HIDE-OPTION CUSTOMIZATION]
        // Purpose: Fetch the full product variant data (cached after first call) so we
        // can find the correct online-eligible variant for the new selection.
        const productData = await fetchProductVariants(variantSelects);
        if (!productData) return;

        const selectedVisibleOptions = getSelectedVisibleOptions(variantSelects, hideOptionIndex);
        const matchedVariant = findOnlineVariant(productData, hideOptionIndex, selectedVisibleOptions);

        if (!matchedVariant) {
          // [HIDE-OPTION CUSTOMIZATION]
          // Purpose: If no online-eligible variant matches the current visible option
          // selection (e.g., a size that only exists for POS), we do not update the
          // selected variant JSON. The theme's unavailable state handling will take over.
          console.warn('[hide-option-variant-filter] No online-eligible variant found for selection:', selectedVisibleOptions);
          return;
        }

        // [HIDE-OPTION CUSTOMIZATION]
        // Purpose: Update the <script data-selected-variant> JSON tag with the matched
        // online-eligible variant. The product-info.js component reads this tag when
        // processing variant changes to update price, SKU, inventory, and media. By
        // updating it here, we ensure those UI elements always reflect online-eligible
        // variant data only.
        const scriptTag = variantSelects.querySelector('script[data-selected-variant]');
        if (scriptTag) {
          scriptTag.textContent = JSON.stringify(matchedVariant);
        }

      }, true); // [HIDE-OPTION CUSTOMIZATION] true = capture phase, runs before theme's bubble-phase listener

      // [HIDE-OPTION CUSTOMIZATION]
      // Purpose: Subscribe to the theme's own variantChange PUB_SUB event (published by
      // product-info.js after it finishes updating the DOM). After product-info.js swaps
      // option value HTML, the hidden "hide" fieldset may be re-rendered. We re-lock it
      // here to ensure it stays hidden and set to "Online" after every variant update cycle.
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, () => {
          lockHideOptionToOnline(variantSelects);
        });
      }
    });
  }

  // [HIDE-OPTION CUSTOMIZATION]
  // Purpose: Defer initialization until the DOM is fully parsed. This ensures all
  // <variant-selects> elements are present before we query for them.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
    /**
   * [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
   * Purpose: King Product Options renders its own option display HTML independently of
   * the theme's Liquid templates, injecting it via Shopify's app embed system
   * (content_for_header). Because it bypasses our Liquid fixes entirely, we must use
   * a DOM-based approach to remove any rendered "hide" option rows that King outputs.
   *
   * King typically renders option name/value pairs inside elements with class names
   * such as .king-po-option, .king-option, or similar. We use a MutationObserver to
   * watch for King's injected content and strip any row whose label text matches "hide"
   * (case-insensitive) as soon as it appears in the DOM.
   *
   * POS SAFETY: This runs only in the browser on the online storefront. POS is unaffected.
   */

  /**
   * [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
   * Purpose: Scan a given root element for any rendered option label/value pair where
   * the label text is "hide" (case-insensitive) and remove its containing row element.
   *
   * We target the broadest reasonable set of selectors to cover King's output regardless
   * of which version or display mode is active. The selectors below cover:
   *   - King's standard option row wrappers (.king-po-option, .king-option-item)
   *   - Generic dt/dd pairs inside .product-option divs (King sometimes reuses theme classes)
   *   - Any element whose text content is exactly "hide:" or "hide" as a label
   *
   * @param {HTMLElement} root - The element to search within (defaults to document.body).
   */
  function stripKingHideOption(root) {
    root = root || document.body;

    // [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
    // Purpose: Target <dt> elements (definition term labels) that King renders for each
    // option name. If the dt text is "hide" (with or without a colon), remove the entire
    // parent container row so neither the label nor the value is visible.
    root.querySelectorAll('dt').forEach(function(dt) {
      var labelText = dt.textContent.trim().toLowerCase().replace(/:$/, '');
      if (labelText === 'hide') {
        // [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
        // Purpose: Walk up to the nearest meaningful container to remove the whole row,
        // not just the <dt>. We try .product-option first (theme class), then the
        // parent element (King's own wrapper), to ensure both label and value are removed.
        var row = dt.closest('.product-option') || dt.parentElement;
        if (row) row.remove();
      }
    });

    // [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
    // Purpose: Also target King-specific class names directly in case King renders its
    // options outside of <dl>/<dt>/<dd> structures. These selectors cover King's known
    // CSS class patterns. If King updates its class names in a future version, add the
    // new class names here.
    var kingOptionSelectors = [
      '.king-po-option',
      '.king-option-item',
      '.king-po-item',
      '[class*="king"][class*="option"]'
    ];

    kingOptionSelectors.forEach(function(selector) {
      try {
        root.querySelectorAll(selector).forEach(function(el) {
          // [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
          // Purpose: Check if this King option element contains a label matching "hide".
          // We check both the element's own text and any child label/span/dt elements.
          var labelEl = el.querySelector('label, span, dt, .king-po-label, .king-option-label');
          var labelText = labelEl
            ? labelEl.textContent.trim().toLowerCase().replace(/:$/, '')
            : el.textContent.trim().toLowerCase().replace(/:$/, '');
          if (labelText === 'hide' || labelText.startsWith('hide:')) {
            el.remove();
          }
        });
      } catch(e) {
        // Silently ignore invalid selectors in older browsers
      }
    });
  }

  /**
   * [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
   * Purpose: Hide King Product Options' (ymq) rendered "hide" option box on the product
   * page. King uses the ymq library and renders each Shopify variant option as a
   * .ymq-options-box element with data-label set to the option name and a name attribute
   * of "option-box-ymq-variant-{optionname}".
   *
   * The CSS rule in base.css handles the immediate visual hide. This JS layer handles:
   * 1. DOM removal (so the element doesn't occupy space or affect tab order)
   * 2. Re-removal after King re-renders on variant changes
   * 3. Disabling the hidden <select> so its value is never submitted in King's form data
   *
   * POS SAFETY: Runs only in the browser. POS is unaffected.
   */

  /**
   * [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
   * Purpose: Find and remove all ymq-rendered elements for the "hide" option using
   * the exact selectors confirmed via DevTools inspection. Also disables the hidden
   * <select> inside so King does not include "hide" in its add-to-cart payload
   * (the native Shopify variant form already handles this correctly via our
   * product-variant-picker.liquid changes).
   */
  function stripYmqHideOption() {
    // [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
    // Purpose: Target the ymq option box using data-label="hide" — the most reliable
    // selector since King sets this directly from the Shopify option name. Also target
    // by name attribute as a secondary selector. Both confirmed present in the inspected HTML.
    var selectors = [
      '[data-label="hide"]',
      '[data-label="Hide"]',
      '[name="option-box-ymq-variant-hide"]'
    ];

    selectors.forEach(function(selector) {
      document.querySelectorAll(selector).forEach(function(el) {
        // [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
        // Purpose: Disable the <select> inside before removing the container. This
        // prevents King's form serialization from including the "hide" option value
        // in the cart line item properties, which would expose it in order data.
        var select = el.querySelector('select');
        if (select) {
          select.disabled = true;
          select.name = '';
        }

        // [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
        // Purpose: Also clear the hidden input King uses to track empty selections,
        // preventing it from submitting any value for the "hide" option.
        var hiddenInput = el.querySelector('.ymq-hidden-input');
        if (hiddenInput) {
          hiddenInput.disabled = true;
          hiddenInput.name = '';
        }

        // [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
        // Purpose: Remove the element from the DOM entirely. Combined with the CSS
        // display:none rule, this ensures the option is invisible and takes up no
        // space even during the brief window between King's render and this JS running.
        el.remove();
      });
    });
  }

  /**
   * [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
   * Purpose: Initialize ymq hide-option stripping with a MutationObserver and polling
   * fallback. King re-renders its entire option UI asynchronously after page load and
   * after each variant change, so we must re-strip on every render cycle.
   */
  function initKingHideOptionObserver() {
    // [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
    // Purpose: Immediate pass on init.
    stripYmqHideOption();

    var debounceTimer = null;
    function debouncedStrip() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(stripYmqHideOption, 30);
    }

    // [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
    // Purpose: MutationObserver on document.body to catch King's async re-renders.
    // King replaces its option container innerHTML on variant changes, which triggers
    // childList mutations on the subtree.
    new MutationObserver(function(mutations) {
      var hasAddedNodes = mutations.some(function(m) { return m.addedNodes.length > 0; });
      if (hasAddedNodes) debouncedStrip();
    }).observe(document.body, { childList: true, subtree: true });

    // [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
    // Purpose: Polling fallback for the first 5 seconds after page load. Runs every
    // 200ms to catch King's initial render regardless of script execution order.
    var pollCount = 0;
    var pollInterval = setInterval(function() {
      stripYmqHideOption();
      if (++pollCount >= 25) clearInterval(pollInterval);
    }, 200);

    // [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
    // Purpose: Re-strip after theme variant change events since King re-renders
    // in response to the same events.
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function() {
        setTimeout(stripYmqHideOption, 100);
      });
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, function() {
        setTimeout(stripYmqHideOption, 150);
      });
    }
  }

  // [HIDE-OPTION CUSTOMIZATION — KING PRODUCT OPTIONS COMPATIBILITY]
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initKingHideOptionObserver);
  } else {
    initKingHideOptionObserver();
  }

})();
